import {Assert, GetPropChanges, IsNaN} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useCallback, useEffect, useRef} from "react";
import {UPlot} from "react-uplot";
import uPlot from "uplot";
import {AnnotationsPlugin} from "uplot-vplugins";
import useResizeObserver from "use-resize-observer";
import {Session} from "../../../../../Server/DB/sessions/@Session.js";
import {GetTestSegments} from "../../../../../Server/DB/testSegments.js";
import {store} from "../../../../../Store/index.js";
import {GetSelectedSessionExternals_AllowingResim} from "../../../../../Store/main/timeline/SessionDerivativeData.js";
import {InAndroid} from "../../../../../Utils/Bridge/Bridge_Native.js";
import {ES} from "web-vcore";
import {SessionDataProcessor} from "../../../../@Shared/Processors/SessionDataProcessor.js";
import {Muse_eegSamplesPerSecond_raw} from "../../../../Tools/@Shared/MuseInterface/EEGStructs.js";
import {currentSeekChart_forceUpdate} from "../SeekBar.js";
import {GetDetailChartAnnotations} from "./DetailChart/DetailChartAnnotations.js";
import {GetDetailChartOptions} from "./DetailChart/DetailChartOptions.js";
import {useDetailChartUIEventListeners} from "./DetailChart/DetailChartUIEventListeners.js";

export const EEGChart_percentOfSamplesGraphed = InAndroid(0) ? .1 : 1;
export const EEGChart_graphedSamplesPerSecond = Muse_eegSamplesPerSecond_raw * EEGChart_percentOfSamplesGraphed;

type ExtInfoForRedraw = ReturnType<typeof GetExtInfoForRedraw>;
function GetExtInfoForRedraw(processor: SessionDataProcessor) {
	const uiState = store.main.timeline.sessions;
	const sessionExt = GetSelectedSessionExternals_AllowingResim();
	if (sessionExt == null) return;
	const testSegments = GetTestSegments(store.main.testing.selectedUser, sessionExt.sessionID);
	return {
		currentTime: uiState.currentTime,
		viewRange_startTime: uiState.viewRange_startTime,
		viewRange_endTime: uiState.viewRange_endTime,
		processingExtent: processor.rawProcessing_nextOffsetFromCenter,
		simulationExtent: processor.simulation_nextOffsetFromStart,
		sessionExt,
		testSegments,
	};
}

export const DetailChart = observer_mgl((props: {
	processor: SessionDataProcessor,
	session: Session,
	initialViewRange: number,
	maxViewRange: number,
})=>{
	const {processor, session, initialViewRange, maxViewRange} = props;
	const uiState = store.main.timeline.sessions;
	const sessionExt = GetSelectedSessionExternals_AllowingResim();
	const maxEEGActivity = sessionExt?.generalHistory.eegActivity.activityByTime.VValues().concat(1).Max() ?? 0;

	// hard-coded, for now
	const yScale_min = -3;
	const yScale_max = 15;

	const {ref: rootRef, width = 100, height = 100} = useResizeObserver();
	const finalWidth = width < 300 ? 300 : width;
	//console.log("DetailChart @width:", width, "@finalWidth:", finalWidth, "height:", height);

	const annotations_atRender = GetDetailChartAnnotations(processor);
	const getExtInfoForRedraw = useCallback(()=>GetExtInfoForRedraw(processor), [processor]);
	const chartOptions_atRender = GetDetailChartOptions(session, finalWidth, height, processor.eegProcessor.minTime_floored, processor.eegProcessor.maxTime_ceilinged, maxViewRange, yScale_min, yScale_max, maxEEGActivity, annotations_atRender ?? [])

	const chartRef = useRef<uPlot>(null);
	// put chartOptions in a ref, so that it can be accessed in the updateChart callback, to update chart-options (for a chart re-render) without a re-render of this wider DetailChart component
	const chartOptions = useRef<uPlot.Options>(chartOptions_atRender);
	chartOptions.current = chartOptions_atRender; // update the ref with the latest options (this may be a re-render, and useRef ignores what's passed into it [other than during the first render])
	//const lastUpdateInfoRef = useRef<ExtInfoForRedraw|null>(null);

	const updateLineVisibilities = useCallback((chartOpts?: uPlot.Options|n)=>{
		const chart = chartRef.current;
		const series = chartOpts?.series ?? chart?.series ?? [];
		for (let i = 0; i < series.length; i++) {
			const newShow = !uiState.disabledSeries.includes(i);
			if (chartOpts) {
				chartOpts.series[i].show = newShow;
			} else {
				if (chart && chart.series[i].show != newShow) {
					chart.setSeries(i, {show: newShow});
				}
			}
		}
	}, [uiState.disabledSeries]);
	updateLineVisibilities(chartOptions.current);

	const updateAnnotations = useCallback(()=>{
		if (chartOptions.current == null) return;
		const annotationsPlugin = chartOptions.current.plugins!.find(a=>a.constructor == AnnotationsPlugin)! as AnnotationsPlugin;
		annotationsPlugin.options.annotations = GetDetailChartAnnotations(processor) ?? [];
	}, [processor]);
	const updateChart = useCallback((newXScale?, newZScale?)=>{
		const chart = chartRef.current;
		if (chart == null) return;
		const xScale_old = {min: chart.scales.x.min, max: chart.scales.x.max};

		updateAnnotations();
		updateLineVisibilities();

		chart.batch(()=>{
			Assert([xScale_old.min, xScale_old.max].every(a=>!IsNaN(a)), "Found NaN in new scale values. (EEGChart.tsx)");
			chart.setScale("x", newXScale ?? xScale_old);

			if (newZScale) {
				chart["v_newZMax"] = newZScale.max;
			}
		});
	}, [updateAnnotations, updateLineVisibilities]);

	// Main update effect
	useEffect(()=>{
		let lastUpdate_infoForRedraw = {} as ExtInfoForRedraw|null;
		type ExtraInfo = {maxEEGActivity: number};
		let lastUpdate_extraInfo = {} as ExtraInfo;
		const targetInterval = 250;

		let timerID: number;
		const tick = ()=>{
			const startTime = Date.now();

			const newInfo = getExtInfoForRedraw();
			if (newInfo == null || newInfo.sessionExt == null) return;

			const newExtraInfo = {...lastUpdate_extraInfo} as ExtraInfo;
			const changes = GetPropChanges(newInfo, lastUpdate_infoForRedraw);
			if (changes.length) {
				const newXScale = {min: newInfo.viewRange_startTime / 1000, max: newInfo.viewRange_endTime / 1000};

				newExtraInfo.maxEEGActivity = newInfo.sessionExt.generalHistory.eegActivity.activityByTime.VValues().concat(1).Max();
				let newZScale;
				if (newExtraInfo.maxEEGActivity != lastUpdate_extraInfo?.maxEEGActivity) {
					newZScale = {
						min: 0,
						max: newExtraInfo.maxEEGActivity,
					};
				}

				updateChart(newXScale, newZScale);

				if (currentSeekChart_forceUpdate && uiState.processing_useCurrentConfig && changes.Any(a=>a.key == "simulationExtent")) {
					currentSeekChart_forceUpdate();
				}
				lastUpdate_infoForRedraw = newInfo;
				lastUpdate_extraInfo = newExtraInfo;
			}

			const runTime = Date.now() - startTime;
			let waitTime = targetInterval - runTime;

			if (!processor.processingComplete) {
				const maxCPUTime = uiState.rendering_cpuLimit;
				const waitDurationRelToRunTime = (1 - maxCPUTime) / maxCPUTime;
				const waitTimeRequiredForCPUTimeCap = runTime * waitDurationRelToRunTime;
				waitTime = waitTime.KeepAtLeast(waitTimeRequiredForCPUTimeCap);
				waitTime = waitTime.KeepAtMost(5000);
			}

			timerID = setTimeout(tick, waitTime) as any;
		};

		tick();
		return ()=>{
			clearTimeout(timerID);
			lastUpdate_infoForRedraw = null;
		};
	}, [getExtInfoForRedraw, updateChart, processor.processingComplete, uiState.processing_useCurrentConfig, uiState.rendering_cpuLimit]);

	useDetailChartUIEventListeners(session, processor, chartRef);

	const data = processor.detailChart_uplotData;
	return (
		<div ref={rootRef as any} style={ES({position: "relative", width: "100%", height: "100%"}, {left: 0, top: 0, height: "calc(100% - 75px)"})}>
			<UPlot chartRef={chartRef} options={chartOptions.current} data={data} placeLegendBelowContainer={true}/>
		</div>
	);
});